'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CapacityMetric {
  id: string;
  label: string;
  value: string;
  status: 'available' | 'limited' | 'full';
  icon: string;
  description: string;
}

interface CapacityDashboardProps {
  className?: string;
}

const CapacityDashboard = ({ className = '' }: CapacityDashboardProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const capacityMetrics: CapacityMetric[] = [
    {
      id: 'fabrication',
      label: 'Fabrication',
      value: '3-5 Days',
      status: 'available',
      icon: 'CubeIcon',
      description: 'Standard turnaround for laser cutting and bending'
    },
    {
      id: 'welding',
      label: 'Welding',
      value: '24-48 Hours',
      status: 'available',
      icon: 'FireIcon',
      description: 'Quick-turn welding services available'
    },
    {
      id: 'coating',
      label: 'Powder Coating',
      value: '5-7 Days',
      status: 'limited',
      icon: 'SparklesIcon',
      description: 'Limited slots for custom color matching'
    },
    {
      id: 'semiconductor',
      label: 'Semiconductor',
      value: '2-3 Weeks',
      status: 'available',
      icon: 'CpuChipIcon',
      description: 'Contamination-free processing available'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-success/10 text-success border-success/20';
      case 'limited':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'full':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available':
        return 'CheckCircleIcon';
      case 'limited':
        return 'ClockIcon';
      case 'full':
        return 'XCircleIcon';
      default:
        return 'QuestionMarkCircleIcon';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available':
        return 'Available Now';
      case 'limited':
        return 'Limited Slots';
      case 'full':
        return 'Fully Booked';
      default:
        return 'Unknown';
    }
  };

  if (!isHydrated) {
    return (
      <section className={`py-20 bg-background ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-card rounded-2xl shadow-lg border border-border p-8">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-1/3"></div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-40 bg-muted rounded-xl"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-20 bg-background ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-card rounded-2xl shadow-lg border border-border overflow-hidden">
          <div className="bg-gradient-to-r from-primary to-brand-primary p-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-headline font-bold text-white">
                  Real-Time Capacity Dashboard
                </h2>
                <p className="text-base text-white/90 font-body">
                  Current availability and turnaround times across all services
                </p>
              </div>
              
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <Icon name="ArrowPathIcon" size={20} variant="outline" className="text-white animate-spin" />
                <span className="text-sm font-semibold text-white font-cta">Live Updates</span>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {capacityMetrics.map((metric) => (
                <div
                  key={metric.id}
                  className="relative bg-muted/30 rounded-xl p-6 space-y-4 border border-border hover:shadow-md transition-all duration-base"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                      <Icon name={metric.icon as any} size={24} variant="outline" className="text-primary" />
                    </div>
                    
                    <div className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(metric.status)}`}>
                      <Icon name={getStatusIcon(metric.status) as any} size={14} variant="solid" />
                      <span>{getStatusLabel(metric.status)}</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-lg font-headline font-bold text-foreground">
                      {metric.label}
                    </h3>
                    <div className="text-2xl font-headline font-bold text-primary">
                      {metric.value}
                    </div>
                    <p className="text-sm text-muted-foreground font-body">
                      {metric.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 bg-muted/30 rounded-xl border border-border">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="InformationCircleIcon" size={24} variant="outline" className="text-primary" />
                </div>
                <div className="space-y-2">
                  <h4 className="text-base font-cta font-bold text-foreground">
                    Need Faster Turnaround?
                  </h4>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed">
                    Contact our production team to discuss rush order options and expedited scheduling. We offer 24-hour quick-turn services for qualifying projects with available capacity.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CapacityDashboard;